import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import apiClient from '../config/axios';
import toast from 'react-hot-toast';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { formatIndiaTime, getIndiaTime } from '../config/india-timezone';
import { ChatSkeleton, ChatMessagesSkeleton } from '../components/Skeleton';
import { MessageCircle, Search, Send, MoreVertical, Phone, Video, RefreshCw, FileText, ChevronDown, X } from 'lucide-react';

const Chats = () => {
  const { currentWorkspace } = useWorkspace();
  const location = useLocation();
  const queryClient = useQueryClient();

  // Debug: Log every render
  console.log('🔍 Chats component render - Workspace ID:', currentWorkspace?.id, 'Name:', currentWorkspace?.name);
  const [filteredChats, setFilteredChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [message, setMessage] = useState('');
  const [messages, setMessages] = useState([]);
  const [sending, setSending] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState({ current: 0, total: 0, message: 'Loading messages...' });
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const processedContactRef = useRef(null);

  // Pagination and filtering state
  const [currentPage, setCurrentPage] = useState(1);
  const [chatsPerPage] = useState(20);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedGroup, setSelectedGroup] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  const [showUnreadOnly, setShowUnreadOnly] = useState(false);
  const [showFilters, setShowFilters] = useState(false);

  // Template-related state
  const [selectedTemplateType, setSelectedTemplateType] = useState('stored');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showTemplateDropdown, setShowTemplateDropdown] = useState(false);
  const [templateParams, setTemplateParams] = useState({});
  const [useTemplate, setUseTemplate] = useState(false);


  // 24-hour window state
  const [windowStatus, setWindowStatus] = useState({
    isActive: false,
    lastClientMessageAt: null,
    canSendDirect: false,
    canSendStoredTemplate: false,
    canSendFacebookTemplate: true
  });

  // Contacts state for realtime lookup
  const [contacts, setContacts] = useState([]);
  const contactsRef = useRef([]);
  const [syncingTemplates, setSyncingTemplates] = useState(false);

  // Fetch contacts for name resolution
  const { data: contactsData } = useQuery(
    ['contacts-for-chats', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) return { contacts: [] };

      const headers = { 'x-workspace-id': currentWorkspace.id };
      const response = await apiClient.get('/api/contacts?limit=1000', { headers });
      return response.data;
    },
    {
      enabled: !!currentWorkspace?.id,
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      onError: (error) => {
        console.error('Error loading contacts for chats:', error);
      }
    }
  );

  // Fetch chats using React Query with complex loading logic
  const { data: chatsData, isLoading: loading } = useQuery(
    ['chats', currentWorkspace?.id],
    async () => {
      if (!currentWorkspace?.id) {
        return { chats: [], templates: [], groups: [], tags: [], contacts: [] };
      }

      const headers = { 'x-workspace-id': currentWorkspace.id };

      // Load messages
      let allMessages = [];
      let currentPage = 1;
      const limit = 1000;

      const firstResponse = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: { page: 1, limit, type: 'whatsapp' }
      });

      const totalPages = firstResponse.data?.pagination?.pages || 1;
      allMessages = [...(firstResponse.data?.messages || [])];

      // Load remaining pages
      for (let page = 2; page <= Math.min(totalPages, 5); page++) {
        const response = await apiClient.get('/api/whatsapp/messages', {
          headers,
          params: { page, limit, type: 'whatsapp' }
        });
        allMessages = [...allMessages, ...(response.data?.messages || [])];
      }

      // Group by phone number - normalize to handle different formats
      const chatsMap = {};
      const normalizePhone = (phone) => {
        if (!phone) return null;
        // Remove all non-numeric characters and get last 10 digits
        const cleaned = phone.replace(/[^0-9]/g, '');
        return cleaned.slice(-10);
      };

      allMessages.forEach(msg => {
        // Identify the contact's phone number (not the business number)
        // Use the phone number that has contact information, or intelligent fallback
        let contactPhone;
        if (msg.type === 'whatsapp') {
          // Use the phone number that has contact information, or fallback to the other number
          if (msg.contact_name && msg.from_number) {
            contactPhone = msg.from_number;
          } else if (msg.contact_name && msg.to_number) {
            contactPhone = msg.to_number;
          } else {
            // Fallback: for outgoing use to_number, for incoming use from_number
            contactPhone = msg.is_outgoing ? msg.to_number : msg.from_number;
          }
        } else {
          contactPhone = msg.from_number || msg.to_number;
        }

        // Skip if no valid phone number
        if (!contactPhone) return;

        // Normalize phone for consistent grouping
        const normalizedPhone = normalizePhone(contactPhone);
        if (!normalizedPhone) return;

        // Determine contact name (avoid showing business name)
        const getContactName = (msg, phone, contactsList) => {
          const raw = msg.contact_name;

          // First, try to find in contacts table (most reliable)
          if (contactsList && contactsList.length > 0) {
            const normalizedPhoneForLookup = phone.replace(/[^0-9]/g, '').slice(-10);
            const foundContact = contactsList.find(contact => {
              if (!contact.phone) return false;
              const contactPhoneNormalized = contact.phone.replace(/[^0-9]/g, '').slice(-10);
              return contactPhoneNormalized === normalizedPhoneForLookup;
            });

            if (foundContact && foundContact.name && foundContact.name.trim() && foundContact.name.trim() !== phone) {
              return foundContact.name.trim();
            }
          }

          // Fallback to contact_name from message if available and valid
          if (raw && typeof raw === 'string') {
            const trimmed = raw.trim();

            // If we have a valid contact name that's not the phone number itself, use it
            if (trimmed.length > 0 && trimmed !== phone && !/^nowgray$/i.test(trimmed)) {
              return trimmed;
            }
          }

          // Last resort: return phone number
          return phone;
        };

        const contactName = getContactName(msg, contactPhone, contactsData?.contacts || []);

        // Debug logging for specific phone number
        if (contactPhone === '916803539233' || contactPhone.includes('916803539233')) {
          console.log(`🔍 Debug for ${contactPhone}:`, {
            msgContactName: msg.contact_name,
            resolvedContactName: contactName,
            msgId: msg.id,
            msgType: msg.type
          });
        }

        // Use normalized phone as key but store original for display
        if (!chatsMap[normalizedPhone]) {
          chatsMap[normalizedPhone] = {
            id: contactPhone,
            name: contactName,
            phone: contactPhone,
            phoneNumber: contactPhone,
            normalizedPhone: normalizedPhone,
            messages: [msg],
            lastMessage: msg.message_text || 'Media message',
            lastMessageObj: msg,
            timestamp: formatIndiaTime(msg.created_at, 'relative'),
            unread: msg.is_outgoing ? 0 : (msg.status === 'received' ? 1 : 0),
            unreadCount: msg.is_outgoing ? 0 : (msg.status === 'received' ? 1 : 0),
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`,
            group: msg.contact_group || '',
            tags: msg.contact_tags ? (() => {
              try {
                return typeof msg.contact_tags === 'string' ? JSON.parse(msg.contact_tags) : msg.contact_tags;
              } catch (error) {
                console.warn('Error parsing contact tags:', error);
                return [];
              }
            })() : []
          };
        } else {
          chatsMap[normalizedPhone].messages.push(msg);

          // Update contact info if we get better information (valid contact name)
          const newContactName = getContactName(msg, contactPhone, contactsData?.contacts || []);
          if (newContactName !== contactPhone && newContactName !== chatsMap[normalizedPhone].phone) {
            chatsMap[normalizedPhone].name = newContactName;
            chatsMap[normalizedPhone].avatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(newContactName)}&background=random`;
          }

          if (new Date(msg.created_at) > new Date(chatsMap[normalizedPhone].lastMessageObj.created_at)) {
            chatsMap[normalizedPhone].lastMessage = msg.message_text || 'Media message';
            chatsMap[normalizedPhone].lastMessageObj = msg;
            chatsMap[normalizedPhone].timestamp = formatIndiaTime(msg.created_at, 'relative');
          }
          if (!msg.is_outgoing && msg.status === 'received') {
            chatsMap[normalizedPhone].unread++;
            chatsMap[normalizedPhone].unreadCount++;
          }
        }
      });

      const chatsList = Object.values(chatsMap).sort((a, b) =>
        new Date(b.lastMessageObj.created_at) - new Date(a.lastMessageObj.created_at)
      );

      // Load other data in parallel
      const [templatesRes, facebookTemplatesRes, groupsRes, tagsRes, contactsRes] = await Promise.all([
        apiClient.get('/api/templates', { params: { page: 1, limit: 50 }, headers }).catch((err) => {
          console.warn('Failed to fetch stored templates:', err.message);
          return { data: { templates: [] } };
        }),
        apiClient.get('/api/whatsapp/templates?days=365&useCache=true', { headers }).catch((err) => {
          console.warn('Failed to fetch Facebook templates:', err.message);
          return { data: { templates: [] } };
        }),
        apiClient.get('/api/contacts/groups/list', { headers }).catch(() => ({ data: { groups: [] } })),
        apiClient.get('/api/contacts/tags/list', { headers }).catch(() => ({ data: { tags: [] } })),
        apiClient.get('/api/contacts', { headers }).catch(() => ({ data: { contacts: [] } }))
      ]);

      console.log('📊 Facebook templates response:', {
        status: facebookTemplatesRes?.status,
        dataKeys: Object.keys(facebookTemplatesRes?.data || {}),
        templatesCount: facebookTemplatesRes?.data?.templates?.length,
        fullData: facebookTemplatesRes?.data
      });

      const facebookTemplatesList = facebookTemplatesRes.data.templates || [];
      console.log('📊 Facebook templates list:', facebookTemplatesList.length, 'templates');

      if (facebookTemplatesList.length > 0) {
        console.log('📊 First Facebook template:', facebookTemplatesList[0]);
      } else {
        console.warn('⚠️ No Facebook templates loaded - check WhatsApp API configuration');
      }

      return {
        chats: chatsList,
        templates: templatesRes.data.templates || [],
        facebookTemplates: facebookTemplatesList,
        groups: groupsRes.data.groups || [],
        tags: tagsRes.data.tags || [],
        contacts: contactsRes.data.contacts || []
      };
    },
    {
      enabled: !!currentWorkspace?.id,
      onSuccess: async (data) => {
        console.log('✅ Chats data loaded:', {
          chatsCount: data.chats?.length,
          templatesCount: data.templates?.length,
          facebookTemplatesCount: data.facebookTemplates?.length,
          contactsCount: data.contacts?.length
        });
        setContacts(data.contacts);
        contactsRef.current = data.contacts;
        setTemplates(data.templates || []);
        setFacebookTemplates(data.facebookTemplates || []);
        console.log('✅ Facebook templates set:', data.facebookTemplates?.length || 0);

        // Auto-sync templates if none are cached
        if ((!data.facebookTemplates || data.facebookTemplates.length === 0) && !syncingTemplates) {
          console.log('📊 No Facebook templates cached, auto-syncing from Facebook API...');
          setSyncingTemplates(true);
          try {
            const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
            const syncResponse = await apiClient.post('/api/whatsapp/templates/sync', {}, { headers });
            if (syncResponse.data.success) {
              toast.success(`Auto-synced ${syncResponse.data.saved} Facebook templates`);
              // Reload templates
              const templatesResponse = await apiClient.get('/api/whatsapp/templates?days=365&useCache=true', { headers });
              setFacebookTemplates(templatesResponse.data.templates || []);
              console.log('✅ Auto-sync complete, loaded', templatesResponse.data.templates?.length, 'templates');
            }
          } catch (syncError) {
            console.error('❌ Auto-sync failed:', syncError);
          } finally {
            setSyncingTemplates(false);
          }
        }
      },
      onError: (error) => {
        console.error('Error loading chats:', error);
        toast.error('Failed to load chats');
      }
    }
  );

  // Keep local state for realtime updates
  const [chats, setChats] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [facebookTemplates, setFacebookTemplates] = useState([]);

  // Update local state when React Query data changes
  useEffect(() => {
    if (chatsData?.chats) setChats(chatsData.chats);
    if (chatsData?.templates) setTemplates(chatsData.templates);
  }, [chatsData]);

  const availableGroups = chatsData?.groups || [];
  const availableTags = chatsData?.tags || [];

  // Simple function to calculate 24-hour window status
  const calculateWindowStatus = (chat) => {
    if (!chat || !chat.messages || chat.messages.length === 0) {
      return {
        isActive: false,
        lastClientMessageAt: null,
        timeRemaining: 0,
        canSendDirect: false,
        canSendStoredTemplate: false,
        canSendFacebookTemplate: true
      };
    }

    // Find the last client message (incoming message)
    const clientMessages = chat.messages.filter(msg =>
      msg.type === 'whatsapp' &&
      msg.from_number &&
      !msg.is_outgoing
    );

    if (clientMessages.length === 0) {
      return {
        isActive: false,
        lastClientMessageAt: null,
        timeRemaining: 0,
        canSendDirect: false,
        canSendStoredTemplate: false,
        canSendFacebookTemplate: true
      };
    }

    // Get the most recent client message
    const lastClientMessage = clientMessages.sort((a, b) =>
      new Date(b.created_at) - new Date(a.created_at)
    )[0];

    // Simple calculation: last message time + 24 hours
    const lastMessageTime = new Date(lastClientMessage.created_at);
    const now = new Date();

    // Always calculate from the message time + 24 hours
    const expiryTime = new Date(lastMessageTime.getTime() + (24 * 60 * 60 * 1000)); // Add 24 hours
    const timeDiff = expiryTime - now;
    const hoursRemaining = Math.max(0, timeDiff / (1000 * 60 * 60));

    // Check if message is in the future (server time issue)
    const isMessageInFuture = lastMessageTime > now;

    if (isMessageInFuture) {
      console.log('⚠️ Message is in the future, but calculating normally');
    }

    // Manual verification calculation
    const manualElapsed = (now.getTime() - lastMessageTime.getTime()) / (1000 * 60 * 60);
    const manualRemaining = Math.max(0, 24 - manualElapsed);

    console.log('🔍 Manual Verification:', {
      manualElapsed: manualElapsed,
      manualRemaining: manualRemaining,
      isMessageInFuture: isMessageInFuture,
      finalHoursRemaining: hoursRemaining,
      status: isMessageInFuture ? 'Future message - 24h window active' : 'Normal calculation'
    });

    // Debug logging with IST conversion
    const istOffset = 5.5 * 60 * 60 * 1000; // IST is UTC+5:30
    const lastMessageIST = new Date(lastMessageTime.getTime() + istOffset);
    const nowIST = new Date(now.getTime() + istOffset);

    // Calculate elapsed time since message
    const elapsedTime = now.getTime() - lastMessageTime.getTime();
    const elapsedHours = elapsedTime / (1000 * 60 * 60);
    const expectedRemaining = Math.max(0, 24 - elapsedHours);

    console.log('🕐 24h Window Debug:', {
      rawMessageTime: lastClientMessage.created_at,
      lastMessageUTC: lastMessageTime.toISOString(),
      lastMessageIST: lastMessageIST.toISOString(),
      nowUTC: now.toISOString(),
      nowIST: nowIST.toISOString(),
      elapsedTime: elapsedTime,
      elapsedHours: elapsedHours,
      expectedRemaining: expectedRemaining,
      actualHoursRemaining: hoursRemaining,
      currentYear: now.getFullYear(),
      messageYear: lastMessageTime.getFullYear(),
      isMessageInFuture: isMessageInFuture
    });

    return {
      isActive: hoursRemaining > 0,
      lastClientMessageAt: lastMessageTime.toISOString(),
      canSendDirect: hoursRemaining > 0,
      canSendStoredTemplate: hoursRemaining > 0,
      canSendFacebookTemplate: true
    };
  };


  // Function to refresh current chat messages
  const refreshCurrentChat = async () => {
    if (!selectedChat) return;

    try {
      setLoadingMessages(true);
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get('/api/whatsapp/messages', {
        headers,
        params: {
          phoneNumber: selectedChat.phone,
          limit: 100,
          type: 'whatsapp'
        }
      });

      if (response.data?.messages) {
        const sortedMessages = response.data.messages.sort((a, b) =>
          new Date(b.created_at) - new Date(a.created_at)
        );
        setMessages(sortedMessages);
        toast.success('Messages refreshed');
      }
    } catch (error) {
      console.error('Error refreshing chat messages:', error);
      toast.error('Failed to refresh messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  // Function to select a chat
  const handleChatSelect = async (chat) => {
    setSelectedChat(chat);

    // Sort messages by timestamp in descending order (newest first)
    const sortedMessages = (chat.messages || []).sort((a, b) => {
      return new Date(b.created_at) - new Date(a.created_at);
    });
    setMessages(sortedMessages);

    // Calculate 24-hour window status for this chat
    const newWindowStatus = calculateWindowStatus(chat);
    setWindowStatus(newWindowStatus);

    // Mark messages as read when chat is selected
    if (chat.unread > 0) {
      try {
        // Update unread count in local state immediately for better UX
        setChats(prevChats =>
          prevChats.map(c =>
            c.id === chat.id ? { ...c, unread: 0 } : c
          )
        );

        // Also update filtered chats if they exist
        setFilteredChats(prevFiltered =>
          prevFiltered.map(c =>
            c.id === chat.id ? { ...c, unread: 0 } : c
          )
        );

        // Mark messages as read in the database
        await apiClient.post('/api/whatsapp/messages/mark-read', {
          phoneNumber: chat.phone
        }, {
          headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {}
        });

        console.log('✅ Marked messages as read for:', chat.phone);
      } catch (error) {
        console.error('Error marking messages as read:', error);
        // If the API call fails, we still want to keep the local state updated
        // The unread count will be corrected on the next reload
      }
    }

    // Force scroll to bottom when chat is selected
    setTimeout(() => {
      if (messagesContainerRef.current) {
        // Scroll the container to bottom
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
      } else if (messagesEndRef.current) {
        // Fallback to scrollIntoView
        messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
      }
    }, 200);
  };

  // Auto-select chat when navigated from Contacts page
  useEffect(() => {
    // Only run if there's a contact to open AND data is loaded (not loading)
    if (!location.state?.openContact || loading || !chatsData) return;

    const contactPhone = location.state.openContact;
    const contactName = location.state.contactName;

    // Normalize phone numbers for comparison (last 10 digits)
    const normalizePhone = (p) => p?.replace(/[^0-9]/g, '').slice(-10) || '';
    const targetPhoneLast10 = normalizePhone(contactPhone);

    // Check if we've already processed this contact to prevent infinite loops
    if (processedContactRef.current === targetPhoneLast10) {
      console.log('🔄 Already processed this contact, skipping:', contactName, contactPhone);
      return;
    }

    // Use chatsData directly (source of truth) instead of local state
    const chatsList = chatsData.chats || [];

    console.log('🎯 Auto-selecting chat for contact:', contactName, contactPhone);
    console.log('   Chats from React Query:', chatsList.length, 'Loading:', loading);
    console.log('   Looking for normalized phone:', targetPhoneLast10);

    // Find the chat with matching phone number
    const chatToOpen = chatsList.find(chat => {
      const chatPhoneLast10 = normalizePhone(chat.phone);
      console.log('   Checking chat:', chat.name, 'normalized:', chatPhoneLast10);
      return chatPhoneLast10 === targetPhoneLast10;
    });

    if (chatToOpen) {
      console.log(`✅ Found existing chat for: ${contactName || contactPhone}`);
      console.log('   Messages in chat:', chatToOpen.messages?.length || 0);
      console.log('   Last message:', chatToOpen.lastMessage);

      // Make sure we use the chat from local state to keep proper reference
      const localChat = chats.find(c => normalizePhone(c.phone) === targetPhoneLast10) || chatToOpen;
      handleChatSelect(localChat);

      // Mark as processed
      processedContactRef.current = targetPhoneLast10;
    } else {
      // Only create new chat if data is fully loaded AND we haven't already created one
      const existingLocalChat = chats.find(c => normalizePhone(c.phone) === targetPhoneLast10);

      if (!existingLocalChat) {
        console.log(`📝 No chat history found, creating new chat for: ${contactName || contactPhone}`);
        const newChat = {
          id: contactPhone,
          name: contactName || contactPhone,
          phone: contactPhone,
          phoneNumber: contactPhone,
          normalizedPhone: targetPhoneLast10,
          messages: [],
          lastMessage: 'No messages yet',
          timestamp: 'New conversation',
          unread: 0,
          unreadCount: 0,
          avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName || contactPhone)}&background=random`,
          group: '',
          tags: []
        };

        // Add to chats list at the top
        setChats(prevChats => [newChat, ...prevChats]);

        // Select the new chat
        handleChatSelect(newChat);

        // Mark as processed
        processedContactRef.current = targetPhoneLast10;

        console.log(`✅ Created and selected new chat for: ${contactName || contactPhone}`);
      } else {
        console.log(`✅ Using existing local chat for: ${contactName || contactPhone}`);
        handleChatSelect(existingLocalChat);

        // Mark as processed
        processedContactRef.current = targetPhoneLast10;
      }

    }

    // Clear the location state to prevent re-selecting on refresh
    window.history.replaceState({}, document.title);
  }, [chatsData, location.state, loading]); // Removed 'chats' from dependencies to prevent infinite loop

  // Reset processed contact ref when location state changes
  useEffect(() => {
    processedContactRef.current = null;
  }, [location.state?.openContact]);

  // Optimistic realtime update on new message (insert into UI without reload)
  useEffect(() => {
    console.log('🎯 Setting up SSE listener (useEffect triggered)');

    const onNewMessage = (e) => {
      const data = e.detail;
      console.log('🔔 Real-time message received:', data);

      if (data?.workspaceId && currentWorkspace?.id && data.workspaceId !== currentWorkspace.id) {
        console.log('❌ Wrong workspace, ignoring');
        return;
      }

      const phone = data?.fromNumber || data?.toNumber;
      if (!phone) {
        console.log('❌ No phone number in message');
        return;
      }

      // Use the actual created_at from database, fallback to current time
      const messageTimestamp = data.createdAt || new Date().toISOString();
      console.log('⏰ Message timestamp:', messageTimestamp, 'from server:', !!data.createdAt);

      // Look up contact information
      const contact = findContact(phone);
      console.log('🔍 Contact lookup result:', { phone, contact, hasContact: !!contact });

      const contactName = contact ? contact.name : phone;
      const contactGroup = contact ? contact.group_name : '';
      const contactTags = contact ? contact.tags : [];

      // Contact details processed

      const incoming = {
        id: `tmp-${Date.now()}`,
        type: 'whatsapp',
        from_number: data.fromNumber || null,
        to_number: data.toNumber || null,
        message_text: data.messageText || '',
        status: '',
        created_at: messageTimestamp,  // Use actual database timestamp
        contact_name: contactName,
      };

      // Normalize phone numbers for comparison (last 10 digits)
      const normalizePhone = (p) => p?.replace(/[^0-9]/g, '').slice(-10) || '';
      const incomingPhoneLast10 = normalizePhone(phone);

      console.log('📞 Incoming phone (last 10):', incomingPhoneLast10);
      console.log('📱 Selected chat phone:', selectedChat?.phone);
      console.log('📱 Selected chat phone (last 10):', selectedChat ? normalizePhone(selectedChat.phone) : 'none');

      setChats(prev => {
        const exists = prev.find(c => normalizePhone(c.phone) === incomingPhoneLast10);
        let updatedChats;

        if (exists) {
          // Update existing chat and move it to the top
          // CRITICAL: Never replace a proper name with a phone number
          let finalName = exists.name;
          let finalGroup = exists.group;
          let finalTags = exists.tags;
          let finalAvatar = exists.avatar;

          if (contact) {
            // We have valid contact info - update if the name is not just a phone number
            if (contact.name && contact.name.trim() && contact.name.trim() !== phone) {
              finalName = contact.name.trim();
              // Only regenerate avatar if we have a proper name
              finalAvatar = `https://ui-avatars.com/api/?name=${encodeURIComponent(contact.name.trim())}&background=random`;
            }
            if (contactGroup) {
              finalGroup = contactGroup;
            }
            if (contactTags && contactTags.length > 0) {
              finalTags = contactTags;
            }
          }
          // else: No contact found - keep ALL existing data (name, avatar, group, tags)

          console.log('📝 Chat update decision:', {
            existingName: exists.name,
            newContactName: contact?.name,
            finalNameUsed: finalName,
            avatarUpdated: finalAvatar !== exists.avatar,
            reason: contact ? 'Contact found' : 'No contact - keeping existing'
          });

          const updatedChat = {
            ...exists,
            name: finalName,
            avatar: finalAvatar,
            group: finalGroup,
            tags: finalTags,
            messages: [incoming, ...(exists.messages || [])],
            lastMessage: incoming.message_text || 'Media message',
            timestamp: 'just now',
            unread: (selectedChat && normalizePhone(selectedChat.phone) === incomingPhoneLast10) ? 0 : ((exists.unread || 0) + 1)
          };

          // Remove the existing chat and add it to the top
          const otherChats = prev.filter(c => normalizePhone(c.phone) !== incomingPhoneLast10);
          updatedChats = [updatedChat, ...otherChats];

          // Note: We don't update selectedChat here to avoid triggering re-renders
          // The selectedChat will be synced when the user interacts with it
        } else {
          // Create new chat at the top
          const newChat = {
            id: phone,
            name: contactName,
            phone,
            group: contactGroup,
            tags: contactTags,
            messages: [incoming],
            lastMessage: incoming.message_text || 'Media message',
            timestamp: 'just now',
            unread: (selectedChat && normalizePhone(selectedChat.phone) === incomingPhoneLast10) ? 0 : 1,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`
          };
          updatedChats = [newChat, ...prev];
        }

        // Don't call updateFilteredChats here - let the useEffect handle it
        // This prevents infinite loops when chats state changes

        return updatedChats;
      });

      // Update messages in chat area if this chat is currently selected
      // Use normalized phone comparison to handle different formats
      if (selectedChat && normalizePhone(selectedChat.phone) === incomingPhoneLast10) {
        console.log('✅ Updating messages in chat area for selected chat');
        setMessages(prevMsgs => {
          // Check if this message already exists (prevent duplicates)
          const messageExists = prevMsgs.some(m =>
            m.message_text === incoming.message_text &&
            Math.abs(new Date(m.created_at) - new Date(incoming.created_at)) < 2000 // Within 2 seconds
          );

          if (messageExists) {
            console.log('⚠️ Duplicate message detected, skipping:', incoming.message_text);
            return prevMsgs;
          }

          console.log('📝 Adding new message:', incoming.message_text, 'at', incoming.created_at);

          // Add new message and sort by timestamp (oldest first for proper display)
          const updatedMessages = [incoming, ...prevMsgs];
          const sorted = updatedMessages.sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

          console.log('✅ Messages updated. Total:', sorted.length);

          // Scroll to bottom after adding new message
          setTimeout(() => {
            if (messagesContainerRef.current) {
              messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
            }
          }, 100);

          return sorted;
        });

        // Recalculate window status if this is a client message
        if (incoming.from_number && !incoming.is_outgoing) {
          const updatedChat = {
            ...selectedChat,
            messages: [incoming, ...(selectedChat.messages || [])]
          };
          const newWindowStatus = calculateWindowStatus(updatedChat);
          setWindowStatus(newWindowStatus);
        }
      } else {
        console.log('ℹ️ Chat not selected or phone mismatch');
      }
    };

    window.addEventListener('sse:new-message', onNewMessage);
    console.log('✅ SSE listener attached');

    return () => {
      window.removeEventListener('sse:new-message', onNewMessage);
      console.log('🔴 SSE listener removed (cleanup)');
    };
  }, [currentWorkspace, selectedChat]);

  // Update filtered chats with given chat list
  const updateFilteredChats = useCallback((chatList) => {
    let filtered = [...chatList];

    // Filter by search term (name or phone)
    if (searchTerm) {
      filtered = filtered.filter(chat => {
        const name = chat.name || '';
        const phone = chat.phone || '';
        return name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          phone.includes(searchTerm);
      });
    }

    // Filter by group (if contact has group info)
    if (selectedGroup) {
      filtered = filtered.filter(chat =>
        chat.group === selectedGroup
      );
    }

    // Filter by tag (if contact has tag info)
    if (selectedTag) {
      filtered = filtered.filter(chat =>
        chat.tags && Array.isArray(chat.tags) && chat.tags.includes(selectedTag)
      );
    }

    // Filter by unread status
    if (showUnreadOnly) {
      filtered = filtered.filter(chat => chat.unread > 0);
    }

    // Sort by most recent messages first
    filtered.sort((a, b) => {
      const aTime = (a.messages && a.messages.length > 0) ? new Date(a.messages[0].created_at) : new Date(0);
      const bTime = (b.messages && b.messages.length > 0) ? new Date(b.messages[0].created_at) : new Date(0);
      return bTime - aTime;
    });

    setFilteredChats(filtered);
  }, [searchTerm, selectedGroup, selectedTag, showUnreadOnly]);

  // Filter chats based on search term, group, and tag
  const filterChats = useCallback(() => {
    updateFilteredChats(chats);
  }, [chats, updateFilteredChats]);

  // Filter chats when search term, group, tag, or unread filter changes
  useEffect(() => {
    // Only filter if we have chats to filter
    if (chats.length > 0) {
      filterChats();
    }
  }, [filterChats]);

  // Reset to first page when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, selectedGroup, selectedTag, showUnreadOnly]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    // Use setTimeout to ensure DOM is updated before scrolling
    const scrollToBottom = () => {
      setTimeout(() => {
        if (messagesContainerRef.current) {
          // Scroll the container to bottom
          messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight;
        } else if (messagesEndRef.current) {
          // Fallback to scrollIntoView
          messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    };

    scrollToBottom();
  }, [messages]);

  // Update chats with contact names when contacts data is available
  useEffect(() => {
    if (contactsData?.contacts && chats.length > 0) {
      updateExistingChatNames(contactsData.contacts);
    }
  }, [contactsData?.contacts, chats.length]);

  // Load contacts for realtime lookup
  const loadContacts = async () => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
      const response = await apiClient.get('/api/contacts', { headers });
      console.log('📇 Contacts API response:', response.data);
      console.log('📇 Contacts type:', typeof response.data);
      console.log('📇 Is array:', Array.isArray(response.data));

      const contactsData = response.data.success && Array.isArray(response.data.contacts)
        ? response.data.contacts
        : [];

      console.log(`✅ Loaded ${contactsData.length} contacts for real-time lookup`);
      if (contactsData.length > 0) {
        // Sample contacts loaded
      }

      setContacts(contactsData);
      contactsRef.current = contactsData; // Update ref for real-time access

      console.log('✅ contactsRef updated, length:', contactsRef.current?.length || 0);

      // Update existing chats with contact names
      updateExistingChatNames(contactsData);
    } catch (error) {
      console.error('❌ Failed to load contacts:', error);
      setContacts([]);
      contactsRef.current = [];
    }
  };

  // Update existing chat names when contacts are loaded
  const updateExistingChatNames = (contactsList) => {
    // Ensure contactsList is an array
    if (!Array.isArray(contactsList) || contactsList.length === 0) {
      return;
    }

    setChats(prevChats => {
      // First, remove duplicates and merge chats with same normalized phone
      const chatsMap = {};
      const normalizePhone = (phone) => phone?.replace(/[^0-9]/g, '').slice(-10) || '';

      prevChats.forEach(chat => {
        const normalizedPhone = normalizePhone(chat.phone);
        if (!chatsMap[normalizedPhone]) {
          chatsMap[normalizedPhone] = chat;
        } else {
          // Merge messages and keep the better name
          const existing = chatsMap[normalizedPhone];
          const allMessages = [...(existing.messages || []), ...(chat.messages || [])];

          // Sort messages by timestamp and remove duplicates
          const uniqueMessages = allMessages.filter((msg, index, arr) =>
            arr.findIndex(m => m.id === msg.id) === index
          ).sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

          // Keep the better name (prefer non-phone names)
          const betterName = (chat.name !== chat.phone && existing.name === existing.phone)
            ? chat.name
            : existing.name;

          chatsMap[normalizedPhone] = {
            ...existing,
            name: betterName,
            messages: uniqueMessages,
            lastMessage: uniqueMessages[0]?.message_text || existing.lastMessage,
            lastMessageObj: uniqueMessages[0] || existing.lastMessageObj,
            timestamp: uniqueMessages[0] ? formatIndiaTime(uniqueMessages[0].created_at, 'relative') : existing.timestamp
          };
        }
      });

      // Convert back to array and update contact names
      const mergedChats = Object.values(chatsMap).map(chat => {
        // Find contact name using the provided contacts list
        const last10Digits = chat.phone.replace(/[^0-9]/g, '').slice(-10);
        const contact = contactsList.find(c => {
          if (!c.phone) return false;
          const contactLast10 = c.phone.replace(/[^0-9]/g, '').slice(-10);
          return contactLast10 === last10Digits;
        });

        // Update if we have a better contact name than what's already set
        const contactName = contact ? contact.name : chat.phone;
        const shouldUpdate = contact &&
          contact.name &&
          contact.name.trim() &&
          contact.name.trim() !== chat.phone &&
          (chat.name === chat.phone || chat.name === contact.phone || !chat.name || chat.name.trim() === '');

        // Debug logging for specific phone number
        if (chat.phone === '916803539233' || chat.phone.includes('916803539233')) {
          console.log(`🔍 updateExistingChatNames for ${chat.phone}:`, {
            currentChatName: chat.name,
            contactNameFromTable: contact ? contact.name : 'No contact found',
            shouldUpdate: shouldUpdate,
            contact: contact
          });
        }

        if (shouldUpdate) {
          return {
            ...chat,
            name: contactName,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`
          };
        }
        return chat;
      });

      // Sort by most recent message
      return mergedChats.sort((a, b) => {
        const aTime = (a.messages && a.messages.length > 0) ? new Date(a.messages[0].created_at) : new Date(0);
        const bTime = (b.messages && b.messages.length > 0) ? new Date(b.messages[0].created_at) : new Date(0);
        return bTime - aTime;
      });
    });
  };


  // Helper function to find contact by phone number (returns full contact object)
  const findContact = (phoneNumber) => {
    if (!phoneNumber) {
      console.log('⚠️ findContact: No phone number provided');
      return null;
    }

    // Access current contacts via ref to avoid closure issues
    const currentContacts = contactsRef.current;
    if (!currentContacts || !currentContacts.length) {
      console.log('⚠️ findContact: No contacts loaded in ref, length:', currentContacts?.length || 0);
      return null;
    }

    const last10Digits = phoneNumber.replace(/[^0-9]/g, '').slice(-10);
    console.log(`🔍 findContact: Searching for phone ${phoneNumber} (last 10: ${last10Digits}) in ${currentContacts.length} contacts`);

    const found = currentContacts.find(c => {
      if (!c.phone) return false;
      const contactLast10 = c.phone.replace(/[^0-9]/g, '').slice(-10);
      return contactLast10 === last10Digits;
    });

    if (found) {
      console.log('✅ findContact: Found contact -', { name: found.name, phone: found.phone });
    } else {
      console.log(`❌ findContact: No contact found for ${last10Digits}`);
    }

    return found;
  };

  // Get current page chats
  const getCurrentPageChats = () => {
    const startIndex = (currentPage - 1) * chatsPerPage;
    const endIndex = startIndex + chatsPerPage;
    return filteredChats.slice(startIndex, endIndex);
  };

  // Get total pages
  const getTotalPages = () => {
    return Math.ceil(filteredChats.length / chatsPerPage);
  };

  // Load more chats (next page)
  const loadMoreChats = () => {
    if (currentPage < getTotalPages()) {
      setCurrentPage(prev => prev + 1);
    }
  };

  // Clear all filters
  const clearFilters = () => {
    setSearchTerm('');
    setSelectedGroup('');
    setSelectedTag('');
    setShowUnreadOnly(false);
    setCurrentPage(1);
  };

  // Fetch templates
  const fetchTemplates = async () => {
    try {
      const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};

      // Fetch stored templates
      const storedResponse = await apiClient.get('/api/templates', { headers });
      let storedData = [];
      if (storedResponse.data && Array.isArray(storedResponse.data)) {
        storedData = storedResponse.data;
      } else if (storedResponse.data && storedResponse.data.templates && Array.isArray(storedResponse.data.templates)) {
        storedData = storedResponse.data.templates;
      }
      setTemplates(storedData);

      // Fetch Facebook templates using the same endpoint as Templates page
      try {
        const facebookResponse = await apiClient.get('/api/whatsapp/templates?days=365', { headers });
        let facebookData = [];
        if (facebookResponse.data && facebookResponse.data.templates && Array.isArray(facebookResponse.data.templates)) {
          facebookData = facebookResponse.data.templates;
        } else if (facebookResponse.data && Array.isArray(facebookResponse.data)) {
          facebookData = facebookResponse.data;
        }
        setFacebookTemplates(facebookData);
      } catch (facebookError) {
        console.warn('Failed to fetch Facebook templates:', facebookError.message);
        console.warn('Facebook error details:', facebookError.response?.data);
        setFacebookTemplates([]);
      }
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to fetch templates');
    }
  };

  // Function removed - now using React Query for data loading
  const loadMessages_REMOVED = async () => {
    return; // Deprecated
    try {
      // setLoading(true);

      // Load all messages with pagination
      let allMessages = [];
      let currentPage = 1;
      let hasMorePages = true;
      let totalPages = 1;

      // First, get the total count to show progress
      const firstResponse = await apiClient.get('/api/whatsapp/messages', {
        headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {},
        params: {
          page: 1,
          limit: 1000,
          type: 'whatsapp'
        }
      });

      totalPages = firstResponse.data?.pagination?.pages || 1;
      const totalMessages = firstResponse.data?.pagination?.total || 0;

      setLoadingProgress({
        current: 1,
        total: totalPages,
        message: `Loading messages... (Page 1 of ${totalPages})`
      });

      const firstPageMessages = firstResponse.data?.messages || [];
      allMessages = [...allMessages, ...firstPageMessages];

      // Load remaining pages
      currentPage = 2;
      while (currentPage <= totalPages) {
        setLoadingProgress({
          current: currentPage,
          total: totalPages,
          message: `Loading messages... (Page ${currentPage} of ${totalPages})`
        });

        const response = await apiClient.get('/api/whatsapp/messages', {
          headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {},
          params: {
            page: currentPage,
            limit: 1000,
            type: 'whatsapp'
          }
        });

        const pageMessages = response.data?.messages || [];
        allMessages = [...allMessages, ...pageMessages];

        currentPage++;

        // Safety check to prevent infinite loops
        if (currentPage > 100) {
          console.warn('⚠️ Reached maximum page limit (100), stopping pagination');
          break;
        }
      }


      // Show success message
      if (allMessages.length > 0) {
        toast.success(`Loaded ${allMessages.length} messages from ${totalPages} page${totalPages > 1 ? 's' : ''}`);
      }

      const messageData = allMessages;

      // Group messages by phone number to create chats
      const chatMap = new Map();

      // No need to identify business numbers - we use message structure instead

      messageData.forEach(msg => {
        // For consistent grouping, always use the same phone number for the same contact
        // We'll use the non-null phone number that's not our business number
        let phoneNumber;
        if (msg.type === 'whatsapp') {
          // Use the phone number that has contact information, or fallback to the other number
          if (msg.contact_name && msg.from_number) {
            phoneNumber = msg.from_number;
          } else if (msg.contact_name && msg.to_number) {
            phoneNumber = msg.to_number;
          } else {
            // Fallback to the first non-null number
            phoneNumber = msg.from_number || msg.to_number;
          }
        } else {
          phoneNumber = msg.from_number || msg.to_number;
        }

        const contactName = (() => {
          const raw = msg.contact_name;
          if (raw && typeof raw === 'string') {
            const trimmed = raw.trim();
            // Treat business/self name as empty so we show the number instead
            if (trimmed.length > 0 && !/^nowgray$/i.test(trimmed)) {
              return trimmed;
            }
          }
          return phoneNumber;
        })();

        if (!chatMap.has(phoneNumber)) {
          chatMap.set(phoneNumber, {
            id: phoneNumber,
            name: contactName,
            phone: phoneNumber,
            messages: [],
            lastMessage: '',
            timestamp: '',
            unread: 0,
            avatar: `https://ui-avatars.com/api/?name=${encodeURIComponent(contactName)}&background=random`,
            group: msg.contact_group || '',
            tags: msg.contact_tags ? (() => {
              try {
                return typeof msg.contact_tags === 'string' ? JSON.parse(msg.contact_tags) : msg.contact_tags;
              } catch (error) {
                console.warn('Error parsing contact tags:', error);
                return [];
              }
            })() : []
          });
        }

        const chat = chatMap.get(phoneNumber);

        // Check if this message already exists in the chat to prevent duplicates
        const messageExists = chat.messages.some(existingMsg =>
          existingMsg.id === msg.id ||
          (existingMsg.message_text === msg.message_text &&
            existingMsg.created_at === msg.created_at &&
            existingMsg.from_number === msg.from_number &&
            existingMsg.to_number === msg.to_number)
        );

        if (!messageExists) {
          chat.messages.push(msg);
        }
      });

      // After processing all messages, update chat metadata and count unread messages
      chatMap.forEach((chat, phoneNumber) => {
        // Sort messages within each chat by timestamp (newest first)
        chat.messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));

        // Set last message and timestamp from the newest message
        if (chat.messages.length > 0) {
          chat.lastMessage = chat.messages[0].message_text || 'Media message';
          chat.timestamp = formatIndiaTime(chat.messages[0].created_at, 'relative');
        }

        // Count unread messages - show messages until user replies
        // Reset unread count first
        chat.unread = 0;

        // Sort messages by timestamp (oldest first) to find the last user reply
        const sortedMessages = [...chat.messages].sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

        // Find the last message sent by the business (user reply)
        let lastUserReplyIndex = -1;
        for (let i = sortedMessages.length - 1; i >= 0; i--) {
          const msg = sortedMessages[i];
          // Check if this is a message sent by the business (outgoing)
          // Business messages have: from_number === null AND status === 'sent'
          if (msg.type === 'whatsapp' &&
            msg.from_number === null &&
            msg.status === 'sent') {
            lastUserReplyIndex = i;
            break;
          }
        }

        // Count unread messages (incoming messages after the last user reply)
        sortedMessages.forEach((msg, index) => {
          if (index > lastUserReplyIndex) {
            // Only count incoming messages from customers
            // Customer messages have: from_number !== null AND status === '' (empty)
            if (msg.type === 'whatsapp' &&
              msg.from_number !== null &&
              msg.status === '') {
              chat.unread++;
            }
          }
        });
      });

      const chatList = Array.from(chatMap.values())
        .sort((a, b) => {
          const aTime = a.messages.length > 0 ? new Date(a.messages[a.messages.length - 1].created_at) : new Date(0);
          const bTime = b.messages.length > 0 ? new Date(b.messages[b.messages.length - 1].created_at) : new Date(0);
          return bTime - aTime;
        });

      setChats(chatList);

      // Auto-select chat if navigated from Contacts page
      if (location.state?.openContact) {
        const contactPhone = location.state.openContact;
        const contactName = location.state.contactName;

        // Normalize phone numbers for comparison (last 10 digits)
        const normalizePhone = (p) => p?.replace(/[^0-9]/g, '').slice(-10) || '';
        const targetPhoneLast10 = normalizePhone(contactPhone);

        // Find the chat with matching phone number
        const chatToOpen = chatList.find(chat => {
          const chatPhoneLast10 = normalizePhone(chat.phone);
          return chatPhoneLast10 === targetPhoneLast10;
        });

        if (chatToOpen) {
          console.log(`🎯 Auto-selecting chat for: ${contactName || contactPhone}`);
          handleChatSelect(chatToOpen);
        } else {
          console.log(`⚠️ Chat not found for: ${contactName || contactPhone}`);
        }

        // Clear the location state to prevent re-selecting on refresh
        window.history.replaceState({}, document.title);
      }
    } catch (error) {
      console.error('❌ Failed to load messages:', error);
      console.error('❌ Error response:', error.response?.data);
      console.error('❌ Error status:', error.response?.status);
      toast.error(`Failed to load messages: ${error.response?.data?.error || error.message}`);
    } finally {
      // setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    if (selectedChat && (message.trim() || selectedTemplate)) {
      try {
        setSending(true);

        if (useTemplate && selectedTemplate) {
          // Send template message using the same approach as Templates page
          const payload = {
            phoneNumbers: [selectedChat.phone]
          };

          if (selectedTemplateType === 'stored') {
            // For stored templates, use plain text mode with parameter substitution
            let messageContent = selectedTemplate.content;

            // Replace parameters in the template content
            Object.keys(templateParams).forEach((param, index) => {
              const placeholder = `{{${index + 1}}}`;
              messageContent = messageContent.replace(new RegExp(placeholder, 'g'), templateParams[param]);
            });

            payload.messageText = messageContent;
          } else {
            // For Facebook templates, always use WhatsApp template mode
            payload.templateName = selectedTemplate.name;
            payload.templateLanguage = selectedTemplate.language || 'en_US';
            // Extract content from Facebook template components
            let templateContent = selectedTemplate.content || selectedTemplate.text;

            // If it's a Facebook template, extract from components
            if (!templateContent && selectedTemplate.components) {
              const bodyComponent = selectedTemplate.components.find(comp => comp.type === 'BODY');
              if (bodyComponent && bodyComponent.text) {
                templateContent = bodyComponent.text;
              }
            }

            console.log('🔍 Frontend Template Debug:', {
              templateName: selectedTemplate.name,
              content: selectedTemplate.content,
              text: selectedTemplate.text,
              components: selectedTemplate.components,
              extractedContent: templateContent
            });

            payload.templateContent = templateContent; // Send the actual content for preview

            // Convert parameters to WhatsApp format in strict placeholder order
            // Extract placeholder order from BODY component text (e.g., {{1}}, {{2}})
            const bodyComponent = selectedTemplate.components?.find(c => c.type === 'BODY');
            const placeholderOrder = [];
            if (bodyComponent?.text) {
              const matches = bodyComponent.text.match(/\{\{(\d+)\}\}/g) || [];
              matches.forEach((m) => {
                const idx = Number(m.replace(/[^0-9]/g, ''));
                if (!Number.isNaN(idx)) placeholderOrder.push(idx);
              });
            }

            // Build parameters in exact order {{1}}, {{2}}, ...
            const params = placeholderOrder.map((n) => {
              const value = templateParams[`param${n}`] || '';
              return { type: 'text', text: value };
            });

            // Require parameters if placeholders exist
            if (placeholderOrder.length > 0 && params.some(p => !p.text || !`${p.text}`.trim())) {
              toast.error('Please fill all required template parameters.');
              setSending(false);
              return;
            }

            if (params.length > 0) {
              payload.templateParameters = params;
            }

            // Always send as template, even without parameters
          }


          await apiClient.post('/api/whatsapp/send-bulk', payload, {
            headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {}
          });

          toast.success('Template sent successfully!');
        } else {
          // Send regular message
          await apiClient.post('/api/whatsapp/send-message', {
            phoneNumber: selectedChat.phone,
            messageText: message.trim()
          }, {
            headers: currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {}
          });

          toast.success('Message sent successfully!');
        }

        // Reset form
        setMessage('');
        setSelectedTemplate(null);
        setTemplateParams({});
        setUseTemplate(false);
        setShowTemplateDropdown(false);

        // Move the current chat to the top and reset unread count
        setChats(prevChats => {
          const currentChat = prevChats.find(c => c.id === selectedChat.id);
          if (currentChat) {
            const otherChats = prevChats.filter(c => c.id !== selectedChat.id);
            const updatedChat = {
              ...currentChat,
              unread: 0,
              lastMessage: message.trim() || (selectedTemplate ? `[Template: ${selectedTemplate.name}]` : ''),
              timestamp: 'just now'
            };
            return [updatedChat, ...otherChats];
          }
          return prevChats;
        });

        // Also update filtered chats if they exist
        setFilteredChats(prevFiltered => {
          const currentChat = prevFiltered.find(c => c.id === selectedChat.id);
          if (currentChat) {
            const otherChats = prevFiltered.filter(c => c.id !== selectedChat.id);
            const updatedChat = {
              ...currentChat,
              unread: 0,
              lastMessage: message.trim() || (selectedTemplate ? `[Template: ${selectedTemplate.name}]` : ''),
              timestamp: 'just now'
            };
            return [updatedChat, ...otherChats];
          }
          return prevFiltered;
        });

        // Show loading state and wait for proper response
        setLoadingMessages(true);

        // Reload messages to show the new message immediately
        queryClient.invalidateQueries(['chats', currentWorkspace?.id]);

        // Wait a bit for the server to process and then refresh messages
        setTimeout(async () => {
          await refreshCurrentChat();
        }, 2000);
      } catch (error) {
        console.error('Send message error:', error.response?.data);
        const errorMessage = error.response?.data?.error || error.response?.data?.details?.error?.message || 'Failed to send message';
        toast.error(`Send failed: ${errorMessage}`);
      } finally {
        setSending(false);
      }
    }
  };

  // Helper function to format date headers
  const formatDateHeader = (date) => {
    return formatIndiaTime(date, 'relative');
  };


  // Helper function to group messages by date
  const groupMessagesByDate = (messages) => {
    const groups = {};

    messages.forEach(message => {
      // Extract date DIRECTLY from ISO string to avoid timezone conversion issues
      let dateKey;
      const timestamp = message.created_at;

      if (typeof timestamp === 'string' && timestamp.includes('T')) {
        // ISO format: "2025-09-30T20:17:32.000+05:30"
        // Extract just the date part (YYYY-MM-DD) before the 'T'
        dateKey = timestamp.split('T')[0];
      } else {
        // Fallback: parse as Date object
        const date = new Date(timestamp);
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const day = String(date.getDate()).padStart(2, '0');
        dateKey = `${year}-${month}-${day}`;
      }

      if (!groups[dateKey]) {
        const [year, month, day] = dateKey.split('-');
        groups[dateKey] = {
          date: new Date(parseInt(year), parseInt(month) - 1, parseInt(day)),
          dateKey: dateKey,
          dateHeader: formatDateHeader(message.created_at),
          messages: []
        };
      }

      groups[dateKey].messages.push(message);
    });

    // Convert to array and sort by date (oldest date first)
    const sortedGroups = Object.values(groups).sort((a, b) => a.date - b.date);

    // Sort messages within each group by timestamp (oldest first = ascending)
    sortedGroups.forEach(group => {
      console.log(`🔧 Before sort - Group ${group.dateKey}:`, group.messages.map(m => ({
        text: m.message_text?.substring(0, 15),
        time: m.created_at
      })));

      group.messages.sort((a, b) => {
        const timeA = new Date(a.created_at).getTime();
        const timeB = new Date(b.created_at).getTime();
        const result = timeA - timeB; // Ascending = oldest first
        return result;
      });

      console.log(`✅ After sort - Group ${group.dateKey}:`, group.messages.map(m => ({
        text: m.message_text?.substring(0, 15),
        time: new Date(m.created_at).toLocaleTimeString()
      })));
    });

    return sortedGroups;
  };


  // Template handling functions
  const handleTemplateSelect = (template) => {
    setSelectedTemplate(template);
    setUseTemplate(true);
    setShowTemplateDropdown(false);

    // Initialize template parameters
    const params = {};
    if (template.parameters && Array.isArray(template.parameters)) {
      template.parameters.forEach(param => {
        params[param] = '';
      });
    } else if (template.components) {
      // Extract parameters from Facebook template components
      const bodyComponent = template.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        const paramMatches = bodyComponent.text.match(/\{\{(\d+)\}\}/g);
        if (paramMatches) {
          paramMatches.forEach((match) => {
            const paramNumber = match.replace(/[^0-9]/g, '');
            params[`param${paramNumber}`] = '';
          });
        }
      }
    } else {
      // For stored templates, check for {{1}}, {{2}} patterns
      if (template.content) {
        const paramMatches = template.content.match(/\{\{(\d+)\}\}/g);
        if (paramMatches) {
          paramMatches.forEach((match) => {
            const paramNumber = match.replace(/[^0-9]/g, '');
            params[`param${paramNumber}`] = '';
          });
        }
      }
    }
    setTemplateParams(params);
  };

  const handleTemplateTypeChange = (type) => {
    setSelectedTemplateType(type);
    setSelectedTemplate(null);
    setTemplateParams({});
  };

  const getCurrentTemplates = () => {
    return selectedTemplateType === 'stored' ? templates : facebookTemplates;
  };


  const renderTemplatePreview = () => {
    if (!selectedTemplate) return null;

    let content = selectedTemplate.content || selectedTemplate.name;

    // For Facebook templates, extract content from components
    if (selectedTemplate.components) {
      const bodyComponent = selectedTemplate.components.find(c => c.type === 'BODY');
      if (bodyComponent && bodyComponent.text) {
        content = bodyComponent.text;
      }
    }

    // Replace parameters in content
    Object.keys(templateParams).forEach((param, index) => {
      if (selectedTemplate.components) {
        // Facebook template format: {{1}}, {{2}}, etc.
        const placeholder = `{{${index + 1}}}`;
        content = content.replace(new RegExp(placeholder, 'g'), templateParams[param] || `[${param}]`);
      } else {
        // Stored template format: {param}
        const placeholder = `{${param}}`;
        content = content.replace(new RegExp(placeholder, 'g'), templateParams[param] || `[${param}]`);
      }
    });

    return (
      <div className="mt-2 p-3 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-blue-800">
            Template Preview ({selectedTemplateType === 'stored' ? 'Stored' : 'Facebook'}):
          </span>
          <button
            onClick={() => {
              setSelectedTemplate(null);
              setUseTemplate(false);
              setTemplateParams({});
            }}
            className="text-blue-600 hover:text-blue-800"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <p className="text-sm text-blue-700">{content}</p>
        <p className="text-xs text-blue-600 mt-1">
          Template: {selectedTemplate.name} | Status: {selectedTemplate.status || 'Unknown'}
        </p>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="h-[calc(100vh-12rem)]">
        <ChatSkeleton />
      </div>
    );
  }

  return (
    <div className="h-[92vh] flex bg-gray-50 -m-4 sm:-m-6 lg:-m-8 p-4 sm:p-6 lg:p-8">
      {/* Chat List Sidebar */}
      <div className="w-1/3 bg-white border-r border-gray-200 flex flex-col">
        {/* Header */}
        <div className="p-4 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-4">
            <h1 className="text-xl font-bold text-gray-900">Chats</h1>

            {/* Search Input */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search chats..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-gray-50"
              />
            </div>

            {/* Filter Controls */}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowFilters(!showFilters)}
                className="flex items-center gap-1 px-3 py-2 text-sm bg-gray-100 hover:bg-gray-200 rounded-lg transition-colors"
              >
                <ChevronDown className={`h-4 w-4 transition-transform ${showFilters ? 'rotate-180' : ''}`} />
                Filters
              </button>

              {(searchTerm || selectedGroup || selectedTag || showUnreadOnly) && (
                <button
                  onClick={clearFilters}
                  className="flex items-center gap-1 px-3 py-2 text-sm bg-red-50 hover:bg-red-100 text-red-600 rounded-lg transition-colors"
                >
                  <X className="h-4 w-4" />
                  Clear
                </button>
              )}
            </div>

            <button
              onClick={() => queryClient.invalidateQueries(['chats', currentWorkspace?.id])}
              disabled={loading}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
          </div>

          {/* Results Count */}
          <div className="mt-3 text-sm text-gray-500 font-medium">
            {getCurrentPageChats().length} of {filteredChats.length} chats
            {filteredChats.length !== chats.length && ` (${chats.length} total)`}
          </div>

          {/* Filter Dropdowns */}
          {showFilters && (
            <div className="mt-3 grid grid-cols-1 gap-3 p-4 bg-gray-50 rounded-xl">
              {/* Group Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Group</label>
                <select
                  value={selectedGroup}
                  onChange={(e) => setSelectedGroup(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Groups</option>
                  {availableGroups.map((group, index) => (
                    <option key={typeof group === 'string' ? group : `group-${index}`} value={typeof group === 'string' ? group : group.name || group}>
                      {typeof group === 'string' ? group : group.name || group}
                    </option>
                  ))}
                </select>
              </div>

              {/* Tag Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Tag</label>
                <select
                  value={selectedTag}
                  onChange={(e) => setSelectedTag(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">All Tags</option>
                  {availableTags.map((tag, index) => (
                    <option key={typeof tag === 'string' ? tag : `tag-${index}`} value={typeof tag === 'string' ? tag : tag.name || tag}>
                      {typeof tag === 'string' ? tag : tag.name || tag}
                    </option>
                  ))}
                </select>
              </div>

              {/* Unread Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Unread Chats ({chats.filter(chat => chat.unread > 0).length})
                </label>
                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="unread-filter"
                    checked={showUnreadOnly}
                    onChange={(e) => setShowUnreadOnly(e.target.checked)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <label htmlFor="unread-filter" className="text-sm text-gray-700">
                    Show only unread chats
                  </label>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Chat List */}
        <div className="flex-1 overflow-y-auto">
          {getCurrentPageChats().map((chat) => (
            <div
              key={chat.id}
              onClick={() => handleChatSelect(chat)}
              className={`p-4 cursor-pointer transition-all duration-200 border-l-4 ${selectedChat?.id === chat.id
                ? 'bg-blue-50 border-blue-500 shadow-sm'
                : 'hover:bg-gray-50 border-transparent'
                }`}
            >
              <div className="flex items-center space-x-4">
                <div className="relative">
                  <img
                    src={chat.avatar}
                    alt={chat.name}
                    className="w-12 h-12 rounded-full object-cover"
                  />
                  {chat.unread > 0 && (
                    <div className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {chat.unread > 99 ? '99+' : chat.unread}
                    </div>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between mb-1">
                    <h3 className="text-sm font-semibold text-gray-900 truncate">
                      {chat.name}
                    </h3>
                    <span className="text-xs text-gray-500 font-medium">{chat.timestamp}</span>
                  </div>
                  <p className="text-sm text-gray-600 truncate leading-relaxed">{chat.lastMessage}</p>
                  {chat.group && (
                    <div className="mt-1">
                      <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                        {chat.group}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>
          ))}

          {/* Load More Button */}
          {currentPage < getTotalPages() && (
            <div className="p-6 text-center">
              <button
                onClick={loadMoreChats}
                className="px-6 py-3 bg-blue-600 text-white rounded-xl hover:bg-blue-700 transition-colors font-medium shadow-sm"
              >
                Load More ({filteredChats.length - (currentPage * chatsPerPage)} remaining)
              </button>
            </div>
          )}

          {/* No chats message */}
          {getCurrentPageChats().length === 0 && !loading && (
            <div className="p-8 text-center">
              <div className="bg-gray-100 rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                <MessageCircle className="h-8 w-8 text-gray-400" />
              </div>
              <p className="text-gray-600 font-medium">No chats found</p>
              {(searchTerm || selectedGroup || selectedTag || showUnreadOnly) && (
                <p className="text-sm text-gray-500 mt-2">Try adjusting your filters</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Chat Messages Area */}
      <div className="flex-1 flex flex-col bg-white">
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-3 border-b border-gray-200 bg-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <img
                    src={selectedChat.avatar}
                    alt={selectedChat.name}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                  <div>
                    <h3 className="text-sm font-medium text-gray-900">{selectedChat.name}</h3>
                    <p className="text-xs text-gray-500">{selectedChat.phone}</p>
                    {selectedChat.group && (
                      <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium bg-blue-100 text-blue-700">
                        {selectedChat.group}
                      </span>
                    )}
                  </div>
                </div>
                <button
                  onClick={refreshCurrentChat}
                  disabled={loadingMessages}
                  className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  title="Refresh messages"
                >
                  <RefreshCw className={`h-4 w-4 ${loadingMessages ? 'animate-spin' : ''}`} />
                </button>
              </div>
            </div>

            {/* Messages Area */}
            <div ref={messagesContainerRef} className="flex-1 overflow-y-auto p-6 bg-gray-50">
              {loadingMessages ? (
                <ChatMessagesSkeleton />
              ) : (
                <div className="space-y-6">
                  {messages.length > 0 ? (
                    (() => {
                      // Group messages by date (will be sorted within groups)
                      const groupedMessages = groupMessagesByDate(messages);

                      return groupedMessages.map((group, groupIndex) => (
                        <div key={groupIndex} className="space-y-3">
                          {/* Date Header */}
                          <div className="flex justify-center">
                            <div className="bg-white border border-gray-200 text-gray-600 text-xs px-4 py-2 rounded-full shadow-sm">
                              {group.dateHeader}
                            </div>
                          </div>

                          {/* Messages for this date */}
                          {group.messages.map((msg, index) => {
                            // Determine if message is outgoing (sent by us) or incoming (received from customer)
                            const isOutgoing = msg.type === 'outgoing' ||
                              (msg.to_number && !msg.from_number) ||
                              msg.status === 'sent';
                            return (
                              <div key={`${groupIndex}-${index}`} className={`flex ${isOutgoing ? 'justify-end' : 'justify-start'}`}>
                                <div className={`px-4 py-3 rounded-2xl max-w-xs lg:max-w-md break-words shadow-sm ${isOutgoing
                                  ? 'bg-blue-600 text-white rounded-br-md'
                                  : 'bg-white text-gray-900 border border-gray-200 rounded-bl-md'
                                  }`}>
                                  <p className="text-sm leading-relaxed">{msg.message_text || 'Media message'}</p>
                                  <p className={`text-xs mt-2 ${isOutgoing ? 'text-blue-100' : 'text-gray-500'}`}>
                                    {formatIndiaTime(msg.created_at, 'time-ampm')}
                                  </p>
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()
                  ) : (
                    <div className="text-center text-gray-500 mt-8">
                      <MessageCircle className="mx-auto h-8 w-8 text-gray-300" />
                      <p className="mt-2">No messages yet</p>
                    </div>
                  )}
                  <div ref={messagesEndRef} />
                </div>
              )}
            </div>

            {/* Message Input */}
            <div className="p-4 border-t border-gray-200">
              {/* 24-Hour Window Status */}
              {selectedChat && (
                <div className="mb-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center space-x-2">
                      <div className={`w-3 h-3 rounded-full ${windowStatus.isActive ? 'bg-green-500' : 'bg-red-500'}`}></div>
                      <span className="text-sm font-medium text-gray-700">
                        {windowStatus.isActive ? '24h Window Active' : '24h Window Expired'}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 text-xs text-gray-600">
                    {windowStatus.isActive ? (
                      <span>✅ Direct messages and stored templates enabled</span>
                    ) : (
                      <span>⚠️ Only Facebook templates allowed</span>
                    )}
                  </div>
                </div>
              )}

              {/* Template Selection */}
              <div className="mb-3">
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => setShowTemplateDropdown(!showTemplateDropdown)}
                    disabled={!windowStatus.canSendStoredTemplate && !windowStatus.canSendFacebookTemplate}
                    className="flex items-center space-x-2 px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <FileText className="h-4 w-4" />
                    <span>Send Template</span>
                    <ChevronDown className="h-4 w-4" />
                  </button>


                  {showTemplateDropdown && (
                    <div className="flex-1 relative">
                      <select
                        value={selectedTemplateType}
                        onChange={(e) => handleTemplateTypeChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                      >
                        <option value="stored" disabled={!windowStatus.canSendStoredTemplate}>
                          💾 Stored Templates {!windowStatus.canSendStoredTemplate ? '(Disabled)' : ''}
                        </option>
                        <option value="facebook">
                          📘 Facebook Templates
                        </option>
                      </select>
                    </div>
                  )}
                </div>

                {showTemplateDropdown && (
                  <div className="mt-2">
                    <select
                      value={selectedTemplate ? (selectedTemplate.id || selectedTemplate.name) : ''}
                      onChange={(e) => {
                        const templateId = e.target.value;
                        const template = getCurrentTemplates().find(t =>
                          (t.id && t.id.toString() === templateId) || t.name === templateId
                        );
                        if (template) {
                          handleTemplateSelect(template);
                        }
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    >
                      <option value="">Select a template...</option>
                      {getCurrentTemplates().map((template) => (
                        <option key={template.id || template.name} value={template.id || template.name}>
                          {template.name}
                          {template.status && ` [${template.status}]`}
                          {template.language && ` (${template.language})`}
                        </option>
                      ))}
                    </select>
                    {getCurrentTemplates().length === 0 && (
                      <div className="mt-2">
                        {selectedTemplateType === 'stored' ? (
                          <p className="text-xs text-gray-500 bg-blue-50 border border-blue-200 rounded p-2">
                            💡 No stored templates available. Create templates in the Templates section.
                          </p>
                        ) : (
                          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
                            <p className="text-xs text-yellow-800 mb-2">
                              ⚠️ No Facebook templates cached yet.
                            </p>
                            <button
                              onClick={async () => {
                                setSyncingTemplates(true);
                                try {
                                  const headers = currentWorkspace?.id ? { 'x-workspace-id': currentWorkspace.id } : {};
                                  toast.loading('Syncing Facebook templates...');
                                  const syncResponse = await apiClient.post('/api/whatsapp/templates/sync', {}, { headers });
                                  if (syncResponse.data.success) {
                                    toast.dismiss();
                                    toast.success(`Synced ${syncResponse.data.saved} templates successfully`);
                                    const templatesResponse = await apiClient.get('/api/whatsapp/templates?days=365&useCache=true', { headers });
                                    setFacebookTemplates(templatesResponse.data.templates || []);
                                  }
                                } catch (error) {
                                  toast.dismiss();
                                  toast.error(error.response?.data?.error || 'Failed to sync templates');
                                } finally {
                                  setSyncingTemplates(false);
                                }
                              }}
                              disabled={syncingTemplates}
                              className="w-full bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-3 py-2 rounded-lg hover:from-blue-700 hover:to-indigo-700 flex items-center justify-center gap-2 text-sm disabled:opacity-50 transition-all shadow-md"
                            >
                              <RefreshCw className={`h-4 w-4 ${syncingTemplates ? 'animate-spin' : ''}`} />
                              <span>{syncingTemplates ? 'Syncing...' : 'Sync Templates from Facebook'}</span>
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Template Parameters */}
              {selectedTemplate && Object.keys(templateParams).length > 0 && (
                <div className="mb-4 space-y-3">
                  <p className="text-sm font-medium text-gray-700">Template Parameters:</p>
                  {Object.keys(templateParams).map(param => (
                    <input
                      key={param}
                      type="text"
                      value={templateParams[param]}
                      onChange={(e) => setTemplateParams({
                        ...templateParams,
                        [param]: e.target.value
                      })}
                      placeholder={`Enter ${param}`}
                      className="w-full px-4 py-3 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm bg-white"
                    />
                  ))}
                </div>
              )}


              {/* Template Preview */}
              {renderTemplatePreview()}

              {/* Message Input */}
              <div className="flex items-end space-x-3 p-4 bg-white border-t border-gray-200">
                <input
                  type="text"
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  placeholder={
                    !windowStatus.canSendDirect
                      ? "Only Facebook templates allowed - 24h window expired"
                      : useTemplate
                        ? "Or type a regular message..."
                        : "Type a message..."
                  }
                  className={`flex-1 px-4 py-3 border rounded-2xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none ${!windowStatus.canSendDirect
                    ? 'border-red-300 bg-red-50 text-red-500 cursor-not-allowed'
                    : 'border-gray-200 bg-gray-50'
                    }`}
                  disabled={(!windowStatus.canSendDirect && !useTemplate) || (useTemplate && selectedTemplate)}
                />
                <button
                  onClick={handleSendMessage}
                  disabled={
                    sending ||
                    (!message.trim() && !selectedTemplate) ||
                    (!windowStatus.canSendDirect && !useTemplate) ||
                    (useTemplate && selectedTemplateType === 'stored' && !windowStatus.canSendStoredTemplate)
                  }
                  className={`p-3 rounded-2xl transition-colors shadow-sm ${(!windowStatus.canSendDirect && !useTemplate) ||
                    (useTemplate && selectedTemplateType === 'stored' && !windowStatus.canSendStoredTemplate)
                    ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                >
                  {sending ? (
                    <RefreshCw className="h-5 w-5 animate-spin" />
                  ) : (
                    <Send className="h-5 w-5" />
                  )}
                </button>
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center bg-gray-50">
            <div className="text-center">
              <div className="bg-gray-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-6">
                <MessageCircle className="h-10 w-10 text-gray-400" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">No chat selected</h3>
              <p className="text-sm text-gray-500 max-w-sm">
                Choose a chat from the list to start messaging and view conversation history
              </p>
            </div>
          </div>
        )}
      </div>

    </div>
  );
};

export default Chats;

